import * as React from "react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { Check, X, Info } from "lucide-react";
import { highlightText } from "components/common/utils";
import { timeAgo } from "components/common/utils";
import { getSchoolClassLabel } from "../../common/utils";

interface RequestListItemProps {
  request: any;
  index: number;
  onViewDetails: (request: any) => void;
  onAssignTeacher: (request: any) => void;
  onRejectRequest: (request: any) => void;
  searchQuery?: string;
}

const RequestListItem: React.FC<RequestListItemProps> = ({
  request,
  index,
  onViewDetails,
  onAssignTeacher,
  onRejectRequest,
  searchQuery = "",
}) => {
  const isPending = request.status === "pending";

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-4 p-3 sm:p-4 bg-white rounded-lg shadow-sm hover:bg-gray-50 relative group border border-gray-100">
      {/* Mobile: Layout en colonne, Desktop: Layout en grille */}
      <div className="flex-1 mb-3 lg:mb-0 lg:col-span-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
          <Typography
            variant="body1"
            className="font-bold text-sm sm:text-base"
          >
            {index + 1}. Explication mathématique
          </Typography>
          {!isPending && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-green-100 text-green-800 border border-green-200">
              Traitée
            </span>
          )}
        </div>
        <Typography
          variant="body2"
          className="text-gray-600 text-xs sm:text-sm mb-1"
        >
          {request.student
            ? `${request.student.firstname} ${request.student.lastname}`
            : request.studentEmail}
        </Typography>
        <Typography variant="body2" className="text-gray-500 text-xs">
          {getSchoolClassLabel(request.schoolClass || request.studentClass)}
        </Typography>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center lg:col-span-7 gap-2 sm:gap-4">
        {/* Date */}
        <div className="lg:col-span-2">
          <Typography
            variant="body2"
            className="text-gray-500 text-xs sm:text-sm"
          >
            {timeAgo(new Date(request.createdAt))}
          </Typography>
        </div>

        {/* Actions */}
        <div className="flex space-x-1 sm:space-x-2 lg:col-span-5 lg:justify-end">
          {isPending && (
            <>
              <IconButton
                onClick={() => onAssignTeacher(request)}
                className="text-green-600 hover:bg-green-50"
                size="small"
                title="Assigner professeur"
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  minWidth: { xs: 36, sm: 40 },
                }}
              >
                <Check size={16} />
              </IconButton>

              <IconButton
                onClick={() => onRejectRequest(request)}
                className="text-red-600 hover:bg-red-50"
                size="small"
                title="Rejeter"
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  minWidth: { xs: 36, sm: 40 },
                }}
              >
                <X size={16} />
              </IconButton>
            </>
          )}

          <IconButton
            onClick={() => onViewDetails(request)}
            className="text-gray-600 hover:bg-gray-100"
            size="small"
            title="Voir les détails"
            sx={{
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              minWidth: { xs: 36, sm: 40 },
            }}
          >
            <Info size={16} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

interface PendingRequestsProps {
  requests: any[];
  onViewDetails: (request: any) => void;
  onAssignTeacher: (request: any) => void;
  onRejectRequest: (request: any) => void;
}

const PendingRequests: React.FC<PendingRequestsProps> = ({
  requests,
  onViewDetails,
  onAssignTeacher,
  onRejectRequest,
}) => {
  if (requests.length === 0) {
    return (
      <p className="text-gray-500 text-center text-sm">
        Aucune demande trouvée.
      </p>
    );
  }

  return (
    <>
      {requests.map((request, index) => (
        <RequestListItem
          key={request._id}
          request={request}
          index={index}
          onViewDetails={onViewDetails}
          onAssignTeacher={onAssignTeacher}
          onRejectRequest={onRejectRequest}
          searchQuery=""
        />
      ))}
    </>
  );
};

export default PendingRequests;
